import { Division, Flex, Heading, Icons, Input, Popover, Button, Icon } from "@ir/ui";
import PageHeading from "@/presentation/ui/layouts/page/PageHeader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApprovalFilterPopover } from "./ApprovalFilterPopover";
import { HelpContextRepository } from "@/infrastructure/repositories/helpContext";
import { LocalStorageAdapter } from "@/infrastructure/adapters/localStorage";
import { JsonSerializer } from "@/infrastructure/serializers/JsonSerializer";
import { GetIsFirstTimeDeductionPageUseCase, SetIsFirstTimeDeductionPageUseCase } from "@/core/usecases/helpContext";
import { useApproval } from "../hooks";

const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_LENGTH = 100;

interface PageHeaderProps {
  isOpenSearchInfo: boolean;
  setIsOpenSearchInfo: (isOpen: boolean) => void;
  isOpenFilterPopover: boolean;
  setIsOpenFilterPopover: (isOpen: boolean) => void;
}

const ApprovalsPageHeader = ({
  isOpenSearchInfo,
  setIsOpenSearchInfo,
  isOpenFilterPopover,
  setIsOpenFilterPopover,
}: PageHeaderProps) => {
  const filterRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isInvalidSearch, setIsInvalidSearch] = useState(false);
  const [searchTextTempValue, setSearchTextTempValue] = useState("");
  const { fetchApprovalsList, filterInfo, setFilter, setSelectedApprovals, setCurrentPage } = useApproval();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpenSearchInfo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [setIsOpenSearchInfo]);

  useEffect(() => {
    setTimeout(() => {
      setSearchTextTempValue(filterInfo?.search ?? "");
    }, 0);
  }, [filterInfo?.search]);

  const helpContextRepository = useMemo(
    () => new HelpContextRepository(new LocalStorageAdapter(new JsonSerializer())),
    []
  );

  const updateFirstTimeSearch = useCallback(async () => {
    await new SetIsFirstTimeDeductionPageUseCase(helpContextRepository).execute(true);
  }, [helpContextRepository]);

  /*
    This is used to check if the search is the first time.
    If it is, then the search info popover will be opened.
  */
  const isFirstTimeSearch = useCallback(async () => {
    const isFirstTimePage = await new GetIsFirstTimeDeductionPageUseCase(helpContextRepository).execute();
    if (!isFirstTimePage) {
      setIsOpenSearchInfo(true);
      updateFirstTimeSearch();
    }
  }, [helpContextRepository, setIsOpenSearchInfo, updateFirstTimeSearch]);

  useEffect(() => {
    if (isOpenSearchInfo) {
      updateFirstTimeSearch();
    }
  }, [isOpenSearchInfo, updateFirstTimeSearch]);

  const validateSearch = useCallback((value: string): boolean => {
    const searchValue = value.trim();
    if (searchValue.length === 0) {
      return false;
    } else if (searchValue.length < MIN_SEARCH_LENGTH) {
      return true;
    }

    const searchValueArray = searchValue.split(",");
    for (const value of searchValueArray) {
      if (value.length < MIN_SEARCH_LENGTH) {
        return true;
      }
    }
    return false;
  }, []);

  const handleSearch = useCallback(
    async (value: string) => {
      const searchValue = value
        .trim()
        .split(",")
        .map(value => value.trim())
        .join(",");

      if (searchValue.length === 0) {
        setIsInvalidSearch(false);
        setSearchTextTempValue("");
        setFilter({
          ...filterInfo,
          search: "",
        });
        setSelectedApprovals([]);
        setCurrentPage(1);
        await fetchApprovalsList({ isLoading: false, isRefreshing: true });
        return;
      }

      const isInvalidSearch = validateSearch(searchValue);
      setIsInvalidSearch(isInvalidSearch);
      if (isInvalidSearch) {
        setIsOpenSearchInfo(true);
        return;
      }

      if (!isInvalidSearch && searchValue.length >= MIN_SEARCH_LENGTH) {
        setFilter({
          ...filterInfo,
          search: searchValue,
        });
        setSelectedApprovals([]);
        setCurrentPage(1);
        await fetchApprovalsList({ isLoading: false, isRefreshing: true });
      }
    },
    [setIsInvalidSearch, filterInfo, fetchApprovalsList, setFilter, setIsOpenSearchInfo, validateSearch, setSelectedApprovals, setCurrentPage]
  );

  const handleClear = useCallback(async () => {
    setIsInvalidSearch(false);
    document.getElementById("approval-search")?.focus();
    setSearchTextTempValue("");
    handleSearch("");
    setIsOpenSearchInfo(false);
    setFilter({
      ...filterInfo,
      search: "",
    });
    setSelectedApprovals([]);
    await fetchApprovalsList({ isLoading: false, isRefreshing: true });
  }, [
    setIsInvalidSearch,
    setSearchTextTempValue,
    handleSearch,
    setIsOpenSearchInfo,
    filterInfo,
    setFilter,
    fetchApprovalsList,
    setSelectedApprovals,
  ]);

  return (
    <PageHeading className="approvals-page-heading" borderBottom={true}>
      <Flex justifyContent="space-between" align="center">
        <Heading text="Approval Queue" level={5} className="m-0 font-bold" />
        <Division style={{ position: "relative", width: "400px" }} role="search" aria-label="Search approvals">
          <Input
            size="small"
            type="text"
            placeholder="Search"
            aria-label="Search"
            id="approval-search"
            className="rounded-medium"
            allowClear={true}
            minLength={MIN_SEARCH_LENGTH}
            maxLength={MAX_SEARCH_LENGTH}
            ref={searchInputRef}
            showCharCount={true}
            isWarning={isInvalidSearch}
            value={searchTextTempValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const isInvalidSearch = validateSearch(e.target.value);
              setIsInvalidSearch(isInvalidSearch);
              setSearchTextTempValue(e.target.value);
            }}
            onClear={handleClear}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              setTimeout(() => {
                isFirstTimeSearch();
              }, 300);
              return e;
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                handleSearch(e.currentTarget.value);
                setSearchTextTempValue(e.currentTarget.value);
              }

              if (e.key === ",") {
                const value = e.currentTarget.value;
                const lastChar = value.at(-1);
                if (lastChar === ",") {
                  e.preventDefault();
                  return;
                }
                const lastCommaIndex = value.lastIndexOf(",");
                const lastCommaValue = value.substring(lastCommaIndex + 1);
                if (lastCommaValue.length < MIN_SEARCH_LENGTH) {
                  e.preventDefault();
                  return;
                }
              }
            }}
            onBeforeInputCapture={() => {
              setIsOpenSearchInfo(false);
            }}
            prefix={
              <Popover
                focusable={true}
                isOpen={isOpenSearchInfo}
                placement="bottom left"
                isNonModal={true}
                shouldCloseOnInteractOutside={() => true}
                defaultOpen={true}
                content={
                  <Division className="text-xs text-grey-700" ref={popoverRef}>
                    <ul style={{ listStyleType: "circle", paddingLeft: "16px" }}>
                      <li className="mb-1">
                        Search by <b>check number</b> or <b>invoice number</b>
                      </li>
                      <li className="mb-1">
                        Minimum search length is <b>{MIN_SEARCH_LENGTH} characters</b>
                      </li>
                      <li className="mb-1">
                        Maximum search length is <b>{MAX_SEARCH_LENGTH} characters</b>
                      </li>
                      <li className="mb-0">
                        Search multiple checks or invoices by using comma. Example: <b>12345,3457</b>
                      </li>
                    </ul>
                  </Division>
                }
              >
                <Button
                  variant="outline"
                  size="small"
                  noStyle
                  onPress={() => {
                    setIsOpenSearchInfo(!isOpenSearchInfo);
                  }}
                  aria-label="Open search info"
                >
                  <Icon name={Icons.InfoCircle} size="small" color="grey" style={{ color: "var(--grey-400)" }} />
                </Button>
              </Popover>
            }
            suffix={
              <ApprovalFilterPopover
                isOpenFilterPopover={isOpenFilterPopover}
                setIsOpenFilterPopover={setIsOpenFilterPopover}
                filterRef={filterRef}
              />
            }
          />
        </Division>
      </Flex>
    </PageHeading>
  );
};

export { ApprovalsPageHeader };
